import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, PencilSimple, Ticket, Trash, CreditCard, Money, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError, STATUS } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import CheckinCouponModal from "../components/CheckinCouponModal";
import { ReservationForm } from "./Prenotazioni";

export default function PrenotazioneDettaglio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [res, setRes] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [editing, setEditing] = useState(false);
  const [custCoupons, setCustCoupons] = useState([]);
  const [showCheckinCoupon, setShowCheckinCoupon] = useState(false);
  const [showAddebito, setShowAddebito] = useState(false);
  const [addebitoAmount, setAddebitoAmount] = useState("");

  const load = () => {
    api.get(`/reservations/${id}`).then((r) => setRes(r.data)).catch((e) => toast.error(apiError(e)));
    api.get(`/reservations/${id}/customer-coupons`).then((r) => setCustCoupons(r.data || [])).catch(() => {});
  };

  useEffect(() => {
    load();
    api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!res) return <div className="text-sm text-zinc-400 py-10">Caricamento...</div>;

  const c = res.customer || {};
  const setStatus = async (status) => {
    try {
      await api.post(`/reservations/${id}/status`, { status });
      toast.success("Stato aggiornato");
      load();
      if (status === "in" && !res.coupon_id && res.customer_id) setShowCheckinCoupon(true);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const linkCoupon = async (ccId) => {
    try {
      await api.patch(`/reservations/${id}/coupon`, { cc_id: ccId });
      toast.success(ccId ? "Coupon agganciato alla prenotazione" : "Coupon rimosso dalla prenotazione");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const chargeCard = async () => {
    const amount = parseFloat(String(addebitoAmount).replace(",", "."));
    if (!amount || amount <= 0) { toast.error("Inserisci un importo valido"); return; }
    try {
      await api.post(`/reservations/${id}/charge-card`, { amount });
      toast.success(`Addebito eseguito: € ${amount.toFixed(2)}`);
      setShowAddebito(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveEdit = async (form) => {
    await api.patch(`/reservations/${id}`, {
      date: form.date, time: form.time, party_size: form.party_size,
      room_id: form.room_id, table_id: form.table_id,
      customer_notes: form.customer_notes, internal_notes: form.internal_notes, channel: form.channel,
      etichetta: form.etichetta || "",
    });
    toast.success("Prenotazione aggiornata");
    setEditing(false);
    load();
  };

  const fmt = (d) => d ? new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div className="space-y-4" data-testid="prenotazione-dettaglio">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="btn-indietro" className="p-2 rounded-md border border-zinc-200 bg-white text-zinc-600">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-900">{res.customer_name}</h1>
          <p className="text-sm text-zinc-500">{res.date} · {res.time} · {res.party_size} persone</p>
        </div>
        <StatusBadge status={res.status} className="text-sm px-3 py-1" />
      </div>

      <div className="flex gap-2 flex-wrap">
        {res.status === "da_confermare" && (
          <button onClick={() => setStatus("confermata")} data-testid="det-btn-conferma"
            className="h-11 px-5 rounded-md bg-emerald-600 text-white text-sm font-bold">CONFERMA</button>
        )}
        {(res.status === "da_confermare" || res.status === "confermata") && (
          <>
            <button onClick={() => setStatus("in")} data-testid="det-btn-arrivato"
              className="h-11 px-5 rounded-md bg-blue-600 text-white text-sm font-bold">ARRIVATO</button>
            <button onClick={() => setStatus("no_show")} data-testid="det-btn-noshow"
              className="h-11 px-4 rounded-md bg-white border border-rose-300 text-rose-700 text-sm font-bold">NO-SHOW</button>
            <button onClick={() => setStatus("cancellata")} data-testid="det-btn-cancella"
              className="h-11 px-4 rounded-md bg-white border border-zinc-300 text-zinc-600 text-sm font-bold">CANCELLA</button>
          </>
        )}
        {res.status === "in" && (
          <button onClick={() => setStatus("out")} data-testid="det-btn-out"
            className="h-11 px-5 rounded-md bg-zinc-900 text-white text-sm font-bold">OUT — LIBERA TAVOLO</button>
        )}
        {res.status === "no_show" && res.card?.payment_method_id && (
          <button onClick={() => { setAddebitoAmount(""); setShowAddebito(true); }} data-testid="det-btn-addebita"
            className="h-11 px-4 rounded-md bg-rose-600 text-white text-sm font-bold flex items-center gap-2">
            <CreditCard size={16} weight="bold" /> ADDEBITA CARTA
          </button>
        )}
        <button onClick={() => setEditing(true)} data-testid="det-btn-modifica"
          className="h-11 px-4 rounded-md bg-white border border-zinc-300 text-zinc-700 text-sm font-semibold flex items-center gap-2">
          <PencilSimple size={16} /> Modifica
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <section className="bg-white border border-zinc-200 rounded-lg p-4 space-y-2">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Cliente</h2>
          <div className="text-sm font-semibold text-zinc-900">{c.first_name} {c.last_name}</div>
          <div className="text-sm text-zinc-600">{c.phone || "—"}</div>
          <div className="text-sm text-zinc-600">{c.email || "—"}</div>
          {(res.customer_tags || []).length > 0 && (
            <div className="flex gap-1.5 flex-wrap pt-1" data-testid="det-customer-tags">
              {res.customer_tags.map((t) => (
                <span key={t} data-testid={`det-tag-${t}`}
                  className="text-xs font-medium text-violet-700 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded">{t}</span>
              ))}
            </div>
          )}
          {res.customer_allergie && (
            <div className="text-sm font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5"
              data-testid="det-allergie">Allergie / intolleranze: {res.customer_allergie}</div>
          )}
          {res.customer_no_shows > 0 && (
            <div className="text-sm font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5"
              data-testid="det-noshow">Attenzione: {res.customer_no_shows} no-show precedenti</div>
          )}
          {c.id && (
            <button onClick={() => navigate(`/clienti/${c.id}`)} data-testid="link-cliente"
              className="text-xs font-semibold text-zinc-900 underline">Apri scheda cliente →</button>
          )}
        </section>
        <section className="bg-white border border-zinc-200 rounded-lg p-4 space-y-2">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Prenotazione</h2>
          <div className="text-sm text-zinc-700">Tavolo: <strong>{res.table_number || "Non assegnato"}</strong></div>
          <div className="text-sm text-zinc-700 capitalize">Canale: {res.channel}</div>
          <div className="text-sm text-zinc-700">Arrivo registrato: {fmt(res.arrived_at)}</div>
          <div className="text-sm text-zinc-700">Uscita registrata: {fmt(res.out_at)}{res.auto_out ? " (automatica)" : ""}</div>
          {res.coupon_name && (
            <div className="text-sm font-medium text-emerald-700 flex items-center gap-1.5"
              data-testid="det-coupon-attached">
              <Ticket size={15} weight="bold" /> Coupon: {res.coupon_name}
              {["da_confermare", "confermata"].includes(res.status) && (
                <button onClick={() => linkCoupon(null)} data-testid="det-coupon-remove"
                  title="Rimuovi coupon dalla prenotazione"
                  className="ml-1 p-1 rounded text-rose-500 hover:bg-rose-50 active:scale-95 transition">
                  <Trash size={14} weight="bold" />
                </button>
              )}
            </div>
          )}
          {res.customer_confirmed_at && (
            <div className="text-sm font-medium text-emerald-700 flex items-center gap-1.5"
              data-testid="det-customer-confirmed">
              <CheckCircle size={15} weight="fill" /> Il cliente ha confermato la presenza dal link
            </div>
          )}
          {res.card?.payment_method_id && (
            <div className="text-sm font-medium text-sky-700 flex items-center gap-1.5"
              data-testid="det-card-registered">
              <CreditCard size={15} weight="bold" /> Carta a garanzia: {res.card.brand} •••• {res.card.last4}
            </div>
          )}
          {res.payment_request?.amount > 0 && (
            <div className={`text-sm font-medium flex items-center gap-1.5 ${res.payment_request.status === "pagata" ? "text-emerald-700" : "text-amber-700"}`}
              data-testid="det-payment-status">
              <Money size={15} weight="bold" /> Caparra € {res.payment_request.amount.toFixed(2)}: {res.payment_request.status === "pagata" ? "pagata ✓" : "in attesa di pagamento"}
            </div>
          )}
        </section>
      </div>

      {custCoupons.length > 0 && ["da_confermare", "confermata", "in"].includes(res.status) && (
        <section className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="det-coupon-section">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">
            Coupon attivi del cliente
          </h2>
          <div className="space-y-2">
            {custCoupons.map((cc) => (
              <div key={cc.cc_id} data-testid={`det-coupon-${cc.cc_id}`}
                className="flex items-center gap-3 border border-emerald-200 bg-emerald-50 rounded-lg px-3 py-2">
                <Ticket size={16} weight="bold" className="text-emerald-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-zinc-900 truncate">{cc.coupon_name}</div>
                  <div className="text-xs text-zinc-500">
                    {cc.code}{cc.valid_to ? ` · valido fino al ${cc.valid_to}` : ""}
                  </div>
                </div>
                <button onClick={() => linkCoupon(cc.cc_id)} data-testid={`det-coupon-apply-${cc.cc_id}`}
                  className="h-8 px-3 rounded-md bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 active:scale-95 transition">
                  APPLICA
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-1">Note del cliente</h2>
        <p className="text-sm text-zinc-800" data-testid="note-cliente">{res.customer_notes || "Nessuna nota dal cliente"}</p>
      </section>
      <section className="bg-zinc-50 border border-zinc-200 rounded-lg p-4">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">Note interne (staff)</h2>
        <p className="text-sm text-zinc-800" data-testid="note-interne">{res.internal_notes || "Nessuna nota interna"}</p>
      </section>

      <section className="bg-white border border-zinc-200 rounded-lg">
        <h2 className="px-4 py-3 border-b border-zinc-200 text-xs font-semibold uppercase tracking-wider text-zinc-500">Storico</h2>
        <div className="divide-y divide-zinc-100" data-testid="storico-list">
          {[...(res.history || [])].reverse().map((h, i) => (
            <div key={i} className="px-4 py-2.5 flex items-start gap-3 text-sm">
              <span className="text-xs text-zinc-400 whitespace-nowrap mt-0.5">{fmt(h.at)}</span>
              <span className="text-zinc-700">
                {h.type === "status" ? (
                  <>Stato: <strong>{STATUS[h.from]?.label || h.from}</strong> → <strong>{STATUS[h.to]?.label || h.to}</strong>{h.by ? ` · ${h.by}` : ""}</>
                ) : h.note}
                {h.note && h.type === "status" && <span className="text-zinc-400"> · {h.note}</span>}
              </span>
            </div>
          ))}
        </div>
      </section>

      {showAddebito && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-3"
          onClick={() => setShowAddebito(false)} data-testid="addebito-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}>
            <div>
              <h3 className="font-display text-lg font-bold text-zinc-900">Addebita sulla carta a garanzia</h3>
              <p className="text-sm text-zinc-500 mt-0.5">
                Penale no-show per {res.customer_name} sulla carta {res.card?.brand} •••• {res.card?.last4}.
                L'addebito è immediato e viene registrato nello storico.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-zinc-400">€</span>
              <input type="number" min="1" step="0.5" value={addebitoAmount} autoFocus
                onChange={(e) => setAddebitoAmount(e.target.value)} data-testid="addebito-amount-input"
                placeholder="20.00"
                className="flex-1 h-12 px-3 rounded-md border border-zinc-300 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-rose-600" />
            </div>
            <div className="flex gap-2">
              <button onClick={chargeCard} data-testid="addebito-confirm"
                className="flex-1 h-11 rounded-md bg-rose-600 text-white text-sm font-bold hover:bg-rose-700 active:scale-95 transition">
                ADDEBITA ORA
              </button>
              <button onClick={() => setShowAddebito(false)} data-testid="addebito-cancel"
                className="h-11 px-4 rounded-md border border-zinc-300 text-zinc-600 text-sm font-semibold">
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
      {showCheckinCoupon && (
        <CheckinCouponModal
          res={res}
          onClose={() => setShowCheckinCoupon(false)}
          onApplied={load}
        />
      )}
      {editing && (
        <ReservationForm
          title="Modifica prenotazione"
          submitLabel="Salva modifiche"
          testidPrefix="edit-res"
          rooms={rooms}
          initial={{
            date: res.date, time: res.time, party_size: res.party_size,
            room_id: res.room_id, table_id: res.table_id,
            customer_notes: res.customer_notes, internal_notes: res.internal_notes, channel: res.channel,
            etichetta: res.etichetta || "",
          }}
          onSubmit={saveEdit}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
}
